import { filtrarSociosCessi, OPCIONES_SOCIOS_CESSI, type OpcionSocioCessi } from '@cessi/content/socios';
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';

type Props = {
  id: string;
  value: string;
  onChange: (value: string) => void;
  ayudaId?: string;
};

const MAX_SUGERENCIAS = 12;

export function SocioCombobox({ id, value, onChange, ayudaId }: Props) {
  const listaId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState(value);
  const [abierto, setAbierto] = useState(false);
  const [activo, setActivo] = useState(0);

  useEffect(() => {
    setQuery(value);
  }, [value]);

  const sugerencias = useMemo(() => filtrarSociosCessi(query, MAX_SUGERENCIAS), [query]);

  const seleccionar = (opcion: OpcionSocioCessi) => {
    setQuery(opcion.value);
    onChange(opcion.value);
    setAbierto(false);
  };

  const abrirLista = () => {
    setAbierto(true);
    setActivo(0);
  };

  const onInputChange = (texto: string) => {
    setQuery(texto);
    onChange('');
    abrirLista();
  };

  const onBlur = () => {
    window.setTimeout(() => {
      setAbierto(false);
      const texto = query.trim().toLowerCase();
      const exacta =
        OPCIONES_SOCIOS_CESSI.find(
          (o) =>
            o.value.toLowerCase() === texto ||
            o.razonSocial.toLowerCase() === texto ||
            o.label.toLowerCase() === texto,
        ) ??
        filtrarSociosCessi(query, 1).find(
          (o) =>
            o.value.toLowerCase() === texto ||
            o.razonSocial.toLowerCase() === texto,
        );
      if (exacta) {
        seleccionar(exacta);
        return;
      }
      setQuery(value);
    }, 120);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!abierto && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      abrirLista();
      return;
    }
    if (e.key === 'Escape') {
      setAbierto(false);
      setQuery(value);
      return;
    }
    if (!abierto || sugerencias.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActivo((i) => (i + 1) % sugerencias.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActivo((i) => (i - 1 + sugerencias.length) % sugerencias.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const opcion = sugerencias[activo];
      if (opcion) seleccionar(opcion);
    }
  };

  const ariaActivo = abierto && sugerencias.length > 0 ? `${listaId}-opt-${activo}` : undefined;

  return (
    <div className={`combobox${abierto ? ' combobox--abierto' : ''}`}>
      <input
        ref={inputRef}
        id={id}
        type="text"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={abierto}
        aria-controls={listaId}
        aria-activedescendant={ariaActivo}
        aria-describedby={ayudaId}
        autoComplete="organization"
        placeholder="Escriba para buscar…"
        value={query}
        onChange={(e) => onInputChange(e.target.value)}
        onFocus={abrirLista}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
      />
      {abierto && (
        <ul id={listaId} className="combobox__lista" role="listbox">
          {sugerencias.length === 0 ? (
            <li className="combobox__vacio" role="presentation">
              Sin coincidencias. Probá con otra palabra.
            </li>
          ) : (
            sugerencias.map((o, i) => (
              <li
                key={`${o.value}|${o.razonSocial}`}
                id={`${listaId}-opt-${i}`}
                role="option"
                aria-selected={value === o.value}
                aria-label={
                  o.razonSocial !== o.label ? `${o.label}, ${o.razonSocial}` : o.label
                }
                className={`combobox__opcion${i === activo ? ' combobox__opcion--activa' : ''}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => seleccionar(o)}
              >
                <span className="combobox__opcion-principal">{o.label}</span>
                {o.razonSocial !== o.label && (
                  <span className="combobox__opcion-rs">{o.razonSocial}</span>
                )}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
